import React, { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import styled from "styled-components";

import loader from "~/assets/images/loader.gif";
import useLocaleSegment from "~/hooks/useLocaleSegment";
import availableYearsFor from "~/utils/availableYears";
import { resolveUKRoute } from "~/utils/ukDataset";

/*
 * Styles
 */

const Container = styled.div`
  left: 50%;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
`;

/*
 * Loading Component
 */
function LoadingComponent() {
  return (
    <Container>
      <img src={loader} alt="Loading" />
    </Container>
  );
}

function I18nReadyGate({ children }) {
  const { ready } = useTranslation();
  if (!ready) return <LoadingComponent />;
  return children;
}

// Lazy-loaded pages
const DataExplorer = lazy(() => import("pages/DataExplorer"));
const KeyFindings = lazy(() => import("pages/KeyFindings"));
const KeyFindingsUK = lazy(() => import("~/pages/KeyFindingsUK"));
const KeyFindingsCA = lazy(() => import("~/pages/KeyFindingsCA"));
const ComingSoonCA = lazy(() => import("~/pages/ComingSoonCA"));
const ComingSoonUK = lazy(() => import("~/pages/ComingSoonUK"));
const CreateGroupSurvey = lazy(() => import("pages/CreateGroupSurvey"));
const GroupSurveys = lazy(() => import("pages/GroupSurveys"));
const GroupSurveyResults = lazy(() => import("pages/GroupSurveyResults"));

// Self-survey links open the Data Explorer with every filter at its default. They have to spell
// the defaults out: Filters.js only reseeds them when no filter key is present, and reseeding
// would drop `survey=1`. Each locale's code frame differs, so each needs its own string.
const SELF_SURVEY_FILTERS = {
  default:
    "AGE=30&MF=14&REGION=30&DENSITY=62&EDUCATION=62&INCOME=254&MARITAL=126&ETHNICITY=62&RELTRAD=62&EVB=6&ATTENDANCE=254",
  "/uk":
    "AGEGROUP=30&MF=6&NATION=30&EDUGROUP=30&INCGROUP=510&MARITAL=510&ETHGROUP=62&RELTRAD=30&ATTENDANCE=126&EVB=6",
};

function GroupSurveyGuard({ children }) {
  const { i18n } = useTranslation();
  const isFrCA =
    i18n.language === "fr-CA" ||
    localStorage.getItem("sot-language") === "fr-CA";
  if (isFrCA) {
    return <Navigate to="/ca" replace />;
  }
  return children;
}

// A locale whose survey isn't enabled yet shows a "Coming Soon" page at its root and
// bounces every sub-route back to it.
const surveyEnabled = {
  ca: process.env.REACT_APP_SURVEY_ENABLE_CA === "true",
  uk: process.env.REACT_APP_SURVEY_ENABLE_UK === "true",
};

function ComingSoonGuard({ localeKey, children }) {
  if (!surveyEnabled[localeKey]) {
    return <Navigate to={`/${localeKey}`} replace />;
  }
  return children;
}

// /uk is the Coming Soon page while the locale is disabled, but the Data Explorer stays
// reachable for any year we hold data for.
function UKDataExplorerGuard({ children }) {
  const params = useParams();
  const { year } = resolveUKRoute(params, process.env.REACT_APP_SURVEY_YEAR_UK);

  // Validated regardless of whether the locale is enabled: SurveyData only fetches the years
  // in AVAILABLE_YEARS_UK, so any other year reaches DataExplorer and throws on data[year].
  if (!availableYearsFor("uk").includes(year)) {
    return <Navigate to="/uk" replace />;
  }

  return children;
}

function GroupRedirect() {
  const { id } = useParams();
  const localeSegment = useLocaleSegment();
  const currentGroupId = localStorage.getItem("groupId");

  if (id !== currentGroupId) {
    localStorage.setItem("groupId", id);
    localStorage.removeItem("submissionId");

    Object.keys(localStorage).forEach((key) => {
      if (key.match(/^Q[0-9]+_[0-9]+/)) {
        localStorage.removeItem(key);
      }
    });
  }

  const filters =
    SELF_SURVEY_FILTERS[localeSegment] ?? SELF_SURVEY_FILTERS.default;

  return (
    <Navigate
      to={`${localeSegment}/data-explorer?${filters}&survey=1`}
      replace
    />
  );
}

const AppRoutes = () => (
  <I18nReadyGate>
    <Suspense fallback={<LoadingComponent />}>
      <Routes>
        <Route path="/" element={<KeyFindings locale="us" />} />
        <Route
          path="/uk"
          element={
            surveyEnabled.uk ? (
              <KeyFindingsUK locale="uk" />
            ) : (
              <ComingSoonUK locale="uk" />
            )
          }
        />
        <Route
          path="/ca"
          element={
            surveyEnabled.ca ? (
              <KeyFindingsCA locale="ca" />
            ) : (
              <ComingSoonCA locale="ca" />
            )
          }
        />
        <Route
          path="/data-explorer/:year?/:id?"
          element={<DataExplorer locale="us" />}
        />
        <Route
          path="/uk/data-explorer/:category?/:year?/:id?"
          element={
            <UKDataExplorerGuard>
              <DataExplorer locale="uk" />
            </UKDataExplorerGuard>
          }
        />
        <Route
          path="/ca/data-explorer/:year?/:id?"
          element={
            <ComingSoonGuard localeKey="ca">
              <DataExplorer locale="ca" />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/takesurvey"
          element={
            <Navigate
              to="/data-explorer?AGE=30&MF=14&REGION=30&DENSITY=62&EDUCATION=62&INCOME=254&MARITAL=126&ETHNICITY=62&RELTRAD=62&EVB=6&ATTENDANCE=254&survey=1"
              replace
            />
          }
        />
        <Route
          path="/uk/takesurvey"
          element={
            <ComingSoonGuard localeKey="uk">
              <Navigate
                to={`/uk/data-explorer?${SELF_SURVEY_FILTERS["/uk"]}&survey=1`}
                replace
              />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/create-group-survey"
          element={<CreateGroupSurvey locale="us" />}
        />
        <Route path="/group-surveys" element={<GroupSurveys locale="us" />} />
        <Route path="/groups/:id/results" element={<GroupSurveyResults />} />
        <Route path="/groups/:id" element={<GroupRedirect />} />
        <Route
          path="/ca/create-group-survey"
          element={
            <ComingSoonGuard localeKey="ca">
              <GroupSurveyGuard>
                <CreateGroupSurvey locale="ca" />
              </GroupSurveyGuard>
            </ComingSoonGuard>
          }
        />
        <Route
          path="/ca/group-surveys"
          element={
            <ComingSoonGuard localeKey="ca">
              <GroupSurveyGuard>
                <GroupSurveys locale="ca" />
              </GroupSurveyGuard>
            </ComingSoonGuard>
          }
        />
        <Route
          path="/ca/groups/:id/results"
          element={
            <ComingSoonGuard localeKey="ca">
              <GroupSurveyGuard>
                <GroupSurveyResults />
              </GroupSurveyGuard>
            </ComingSoonGuard>
          }
        />
        <Route
          path="/ca/groups/:id"
          element={
            <ComingSoonGuard localeKey="ca">
              <GroupSurveyGuard>
                <GroupRedirect />
              </GroupSurveyGuard>
            </ComingSoonGuard>
          }
        />
        <Route
          path="/uk/create-group-survey"
          element={
            <ComingSoonGuard localeKey="uk">
              <CreateGroupSurvey locale="uk" />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/uk/group-surveys"
          element={
            <ComingSoonGuard localeKey="uk">
              <GroupSurveys locale="uk" />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/uk/groups/:id/results"
          element={
            <ComingSoonGuard localeKey="uk">
              <GroupSurveyResults />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/uk/groups/:id"
          element={
            <ComingSoonGuard localeKey="uk">
              <GroupRedirect />
            </ComingSoonGuard>
          }
        />
        <Route
          path="/groupsurveys"
          element={<Navigate to="/group-surveys" replace />}
        />
        <Route
          path="/groupsurvey"
          element={<Navigate to="/group-surveys" replace />}
        />
      </Routes>
    </Suspense>
  </I18nReadyGate>
);

export default AppRoutes;
